<template>
  <Content
    class="col-span-full"
    :title="$route.name"
    :showAdd="false"
    :pagination="{
      count:
        activeTab === 'transactions'
          ? transactionsData.count
          : quotasData.count,
      current: +$route.query.p,
      request:
        activeTab === 'transactions' ? 'fuels/transactions/' : 'fuels/quotas/',
    }"
    @tableData="handleTableData"
    @loading="loading = $event"
    @add="showAddForm = true"
  >
    <!-- Main Tabs -->
    <el-tabs
      v-model="activeTab"
      class="mb-4"
      type="card"
      @tab-click="handleMainTabChange"
    >
      <el-tab-pane label="تراکنش‌های سوخت" name="transactions"></el-tab-pane>
      <el-tab-pane label="سهمیه‌های سوخت" name="quotas"></el-tab-pane>
    </el-tabs>

    <tableSearch
      @search="searchService"
      :tagValue="selectedTags.split(',')"
      :searchTag="true"
      :searchValue="searchValue"
      @selectedTags="searchTagInService"
      :loading="searchLoading"
      :placeholder="
        $t('SEARCH_FOR', {
          type: $t('search_for_license_and_fullname'),
        })
      "
    >
      <el-form-item :label="$t('START_DATE')" prop="dateRange.start">
        <date-picker
          v-model="dateRange.start"
          type="date"
          :locale="$i18n.locale"
          format="jYYYY-jMM-jDD"
          @change="handleDateChange"
          color="#151515"
          class="el-input__inner"
          clearable
        />
      </el-form-item>
      <el-form-item :label="$t('END_DATE')" prop="dateRange.end">
        <date-picker
          v-model="dateRange.end"
          type="date"
          :locale="$i18n.locale"
          format="jYYYY-jMM-jDD"
          @change="handleDateChange"
          color="#151515"
          class="el-input__inner"
          clearable
        />
      </el-form-item>
    </tableSearch>

    <!-- Transaction Stats (Only show for transactions tab) -->
    <div
      v-if="
        activeTab === 'transactions' &&
        (transactionsData.real_cost || transactionsData.total_paid_sum)
      "
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6"
    >
      <!-- Real Cost Card -->
      <div class="bg-white rounded-md shadow p-4">
        <div class="flex justify-between items-center">
          <h3 class="text-gray-500 text-sm font-medium">
            {{ $t('REAL_COST') }}
          </h3>
          <el-tooltip
            class="item"
            effect="dark"
            :content="$t('REAL_COST_INFO')"
            placement="top"
          >
            <i class="el-icon-info text-gray-400 cursor-help"></i>
          </el-tooltip>
        </div>
        <div class="mt-2 flex items-center">
          <span
            class="text-2xl font-semibold"
            v-html="
              $options.filters.numberSeperator1(transactionsData.real_cost || 0)
            "
          ></span>
        </div>
      </div>

      <!-- Total Paid Sum Card -->
      <div class="bg-white rounded-md shadow p-4">
        <div class="flex justify-between items-center">
          <h3 class="text-gray-500 text-sm font-medium">
            {{ $t('TOTAL_PAID') }}
          </h3>
        </div>
        <div class="mt-2">
          <span
            class="text-2xl font-semibold"
            v-html="
              $options.filters.numberSeperator1(
                transactionsData.total_paid_sum || 0
              )
            "
          ></span>
        </div>
      </div>
      <div class="bg-white rounded-md shadow p-4">
        <div class="flex justify-between items-center">
          <h3 class="text-gray-500 text-sm font-medium">
            {{ $t('DIFFERENCE_PERCENTAGE') }}
          </h3>
          <el-tooltip
            class="item"
            effect="dark"
            :content="$t('DIFFERENCE_PERCENTAGE_INFO')"
            placement="top"
          >
            <i class="el-icon-info text-gray-400 cursor-help"></i>
          </el-tooltip>
        </div>
        <div class="mt-2 flex items-center">
          <span
            class="text-2xl font-semibold ltr"
            :class="{
              'text-green-500': transactionsData.difference_percentage < 0,
              'text-red-500': transactionsData.difference_percentage > 0,
              'text-gray-500': transactionsData.difference_percentage === 0,
            }"
          >
            {{ transactionsData.difference_percentage > 0 ? '+' : ''
            }}{{ transactionsData.difference_percentage || 0 }}%
          </span>
          <span class="ml-2">
            <i
              v-if="transactionsData.difference_percentage > 0"
              class="el-icon-top text-red-500 text-lg"
            ></i>
            <i
              v-else-if="transactionsData.difference_percentage < 0"
              class="el-icon-bottom text-green-500 text-lg"
            ></i>
          </span>
        </div>
      </div>
    </div>

    <!-- Transactions Table -->
    <div
      v-if="
        transactionsData &&
        transactionsData.results.length &&
        activeTab === 'transactions'
      "
      class="flex justify-end mb-4"
    >
      <el-button
        :loading="isDownloadLoading"
        type="info"
        size="small"
        round
        @click="downloadReport()"
      >
        {{ $t('DOWNLOAD_REPORT') }}
      </el-button>
    </div>
    <TransactionTable
      v-if="activeTab === 'transactions'"
      :tableData="transactionsData"
      :loading="loading"
      @transaction-removed="handleTransactionRemoved"
      @transaction-updated="handleTransactionUpdated"
    />

    <!-- Quotas Table -->
    <QuotasTable
      v-else
      :tableData="quotasData"
      :loading="loading"
      @quota-removed="handleQuotaRemoved"
      @quota-updated="handleQuotaUpdated"
    />

    <!-- Add Transaction Form Modal -->
    <FuelTransactionForm
      v-if="showAddForm"
      :show.sync="showAddForm"
      :formType="activeTab === 'transactions' ? 'fullForm' : 'simpleForm'"
      @transaction-added="handleTransactionAdded"
    />
  </Content>
</template>

<script>
import { getTransaction, fuelsQuotas, downloadTransactions } from '@/api/fuel'
import TransactionTable from '@/components/fuel/TransactionTable'
import QuotasTable from '@/components/fuel/QuotasTable'
import FuelTransactionForm from '@/components/fuel/FuelTransactionForm'
import tableSearch from '@/components/elements/tableSearch'
import { jalaliToGregorian } from '@/mixins/jalaliToGregorian'

export default {
  mixins: [jalaliToGregorian],

  data() {
    return {
      isDownloadLoading: false,
      activeTab: 'transactions',
      transactionsData: {
        results: [],
        count: 0,
      },
      quotasData: {
        results: [],
        count: 0,
      },
      loading: true,
      showAddForm: false,
      searchValue: '',
      selectedTags: '',
      searchLoading: false,
      dateRange: {
        start: null,
        end: null,
      },
    }
  },

  components: {
    TransactionTable,
    QuotasTable,
    FuelTransactionForm,
    tableSearch,
  },

  methods: {
    downloadReport() {
      this.isDownloadLoading = true

      const searchParams = {
        search: this.searchValue,
        company_vehicle__company_vehicle_tags__id__in:
          this.selectedTags || null,
      }

      if (this.dateRange.start) {
        searchParams.date__gte = this.jalaliToGregorian(
          this.dateRange.start,
          this.$i18n.locale
        )
      }

      if (this.dateRange.end) {
        searchParams.date__lte = this.jalaliToGregorian(
          this.dateRange.end,
          this.$i18n.locale
        )
      }

      downloadTransactions
        .api(searchParams)
        .then((res) => {
          this.isDownloadLoading = false
          if (res) {
            var blob = new Blob([res], { type: 'application/octet-stream' })

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = url
            a.download = `fuel-transactions-CARPO.xlsx`
            document.body.appendChild(a)
            a.click()

            window.URL.revokeObjectURL(url)
          }
        })
        .catch((error) => {
          console.error('Download Error:', error)
        })
    },
    handleMainTabChange() {
      // Clear any active form
      this.showAddForm = false

      // Update the URL to reflect the active tab
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab: this.activeTab,
          p: 1, // Reset to first page when switching tabs
        },
      })

      // Reload the data for the selected tab
      this.fetchData()
    },

    handleTableData(data) {
      if (this.activeTab === 'transactions') {
        this.transactionsData = data
      } else {
        this.quotasData = data
      }
    },

    handleDateChange() {
      this.fetchData()
    },

    searchService(val) {
      this.searchValue = val
      this.fetchData()
    },

    searchTagInService(val) {
      this.selectedTags = val.join(',')
      this.fetchData()
    },

    fetchData() {
      this.searchLoading = true
      this.loading = true

      const searchParams = {
        search: this.searchValue,
        company_vehicle__company_vehicle_tags__id__in:
          this.selectedTags || null,
      }

      if (this.dateRange.start) {
        searchParams.date__gte = this.jalaliToGregorian(
          this.dateRange.start,
          this.$i18n.locale
        )
      }

      if (this.dateRange.end) {
        searchParams.date__lte = this.jalaliToGregorian(
          this.dateRange.end,
          this.$i18n.locale
        )
      }

      // Update URL query parameters
      this.$router.replace({
        query: {
          ...this.$route.query,
          search: this.searchValue,
          selectedTags: this.selectedTags,
          dateStart: this.dateRange.start ? this.dateRange.start : undefined,
          dateEnd: this.dateRange.end ? this.dateRange.end : undefined,
          tab: this.activeTab,
        },
      })

      if (this.activeTab === 'transactions') {
        this.fetchTransactions(searchParams)
      } else {
        this.fetchQuotas(searchParams)
      }
    },

    fetchTransactions(searchParams) {
      getTransaction
        .api(searchParams)
        .then((res) => {
          if (res) {
            this.transactionsData = res
          }
        })
        .catch((error) => {
          console.error('Error fetching transactions:', error)
        })
        .finally(() => {
          this.searchLoading = false
          this.loading = false
        })
    },

    fetchQuotas(searchParams) {
      fuelsQuotas
        .api(searchParams)
        .then((res) => {
          if (res) {
            this.quotasData = res
          }
        })
        .catch((error) => {
          console.error('Error fetching fuel quotas:', error)
        })
        .finally(() => {
          this.searchLoading = false
          this.loading = false
        })
    },

    loadInitialData() {
      // Get URL parameters
      const { search, selectedTags, dateStart, dateEnd, tab } =
        this.$route.query

      // Initialize from URL params
      this.searchValue = search || ''
      this.selectedTags = selectedTags || ''
      this.dateRange.start = dateStart || null
      this.dateRange.end = dateEnd || null
      this.activeTab = tab || 'transactions'

      this.fetchData()
    },

    handleTransactionRemoved() {
      this.fetchTransactions({})
    },

    handleTransactionUpdated() {
      this.fetchTransactions({})
    },

    handleTransactionAdded() {
      if (this.activeTab === 'transactions') {
        this.fetchTransactions({})
      } else {
        this.fetchQuotas({})
      }
    },

    handleQuotaRemoved() {
      this.fetchQuotas({})
    },

    handleQuotaUpdated() {
      this.fetchQuotas({})
    },
  },

  created() {
    this.loadInitialData()
  },
}
</script>
